import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DeliverableBadge } from "@/components/Badges";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2, Music as MusicIcon, RefreshCw, Search, Play, Pause, Tag, Pencil, X, ExternalLink,
  Link2, Plus, ListMusic, AlertTriangle, CalendarClock,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SORT_OPTIONS = [
  { value: "recent_used", label: "Recently used" },
  { value: "most_used", label: "Most used" },
  { value: "least_used", label: "Least used" },
  { value: "newest", label: "Recently added" },
  { value: "name", label: "Name (A–Z)" },
];

function fmtUsed(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return null; }
}

function fmtSize(bytes) {
  if (!bytes) return null;
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
}

function StyleChips({ styles = [], className = "" }) {
  if (!styles.length) return null;
  return (
    <div className={`flex flex-wrap gap-1 ${className}`}>
      {styles.map((s) => (
        <span key={s} data-testid={`track-style-${s}`}
          className="rounded-full border border-fuchsia-500/40 bg-fuchsia-500/10 px-2 py-0.5 text-[10px] font-medium text-fuchsia-600 dark:text-fuchsia-300">{s}</span>
      ))}
    </div>
  );
}

function MarkUsedDialog({ track, onDone }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  const runSearch = async () => {
    if (!q.trim()) return;
    setSearching(true);
    try {
      const { data } = await api.get(`/orders?search=${encodeURIComponent(q.trim())}`);
      setResults(data.slice(0, 12));
    } catch (err) { toast.error(apiError(err)); }
    finally { setSearching(false); }
  };

  const markUsed = async (order, deliv) => {
    setSaving(true);
    try {
      await api.post(`/music/${track.id}/usage`, { order_id: order.id, deliverable_id: deliv.id });
      toast.success(`Marked used on ${deliv.type} — ${order.address || order.title}`);
      setOpen(false); setSelected(null); setResults([]); setQ("");
      onDone();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setSelected(null); setResults([]); setQ(""); } }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-8 gap-1.5" data-testid={`mark-used-btn-${track.id}`}>
          <Link2 className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Mark used</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="mark-used-dialog">
        <DialogHeader><DialogTitle>Mark “{track.name}” as used</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input autoFocus placeholder="Search order by address or #number…" value={q}
              data-testid="mark-used-search-input"
              onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()} />
            <Button onClick={runSearch} disabled={searching} data-testid="mark-used-search-btn" className="gap-1.5">
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </Button>
          </div>
          <div className="max-h-[320px] space-y-2 overflow-y-auto">
            {!selected && results.map((o) => (
              <button key={o.id} onClick={() => setSelected(o)} data-testid={`mark-used-order-${o.id}`}
                className="flex w-full items-center justify-between gap-2 rounded-lg border border-border px-3 py-2 text-left text-sm transition-colors hover:bg-secondary/50">
                <span className="truncate">{o.address || o.title}</span>
                <span className="font-mono text-xs text-muted-foreground">#{o.number}</span>
              </button>
            ))}
            {selected && (
              <div className="space-y-2">
                <button onClick={() => setSelected(null)} className="text-xs text-muted-foreground hover:text-foreground">← Back to results</button>
                <div className="text-sm font-medium">{selected.address || selected.title}</div>
                <p className="text-xs text-muted-foreground">Pick the product this track was used on:</p>
                {(selected.deliverables || []).filter((d) => d.due_source !== "excluded").map((d) => (
                  <button key={d.id} onClick={() => markUsed(selected, d)} disabled={saving}
                    data-testid={`mark-used-product-${d.id}`}
                    className="flex w-full items-center gap-2 rounded-lg border border-border px-3 py-2 text-left transition-colors hover:bg-secondary/50 disabled:opacity-50">
                    <DeliverableBadge type={d.type} />
                    <span className="truncate text-sm text-muted-foreground">{d.title}</span>
                  </button>
                ))}
              </div>
            )}
            {!selected && !results.length && (
              <p className="py-6 text-center text-sm text-muted-foreground">Search for a property to attach this track to.</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TrackRow({ track, styleOptions, playingId, setPlayingId, onChanged }) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [styles, setStyles] = useState(track.styles || []);
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState(track.tags || []);
  const [notes, setNotes] = useState(track.notes || "");
  const [saving, setSaving] = useState(false);
  const [pendingRemove, setPendingRemove] = useState(null);
  const audioRef = useRef(null);
  const isPlaying = playingId === track.id;
  const streamUrl = `${BACKEND_URL}/api/music/${track.id}/stream`;

  useEffect(() => {
    if (!isPlaying && audioRef.current) { audioRef.current.pause(); }
  }, [isPlaying]);

  const togglePlay = () => {
    if (isPlaying) { setPlayingId(null); }
    else { setPlayingId(track.id); setTimeout(() => audioRef.current?.play().catch(() => {}), 50); }
  };

  const toggleStyle = (s) => setStyles((cur) => cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]);
  const addTag = () => {
    const v = tagInput.trim();
    if (v && !tags.includes(v)) setTags([...tags, v]);
    setTagInput("");
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/music/${track.id}`, { styles, tags, notes });
      toast.success("Track updated");
      setEditing(false);
      onChanged(data);
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className={`rounded-xl border bg-card p-4 ${track.missing ? "border-amber-500/40" : "border-border"}`} data-testid={`track-row-${track.id}`}>
      <div className="flex items-start gap-3">
        <button onClick={togglePlay} data-testid={`track-play-${track.id}`}
          className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors ${isPlaying ? "bg-fuchsia-500 text-white" : "bg-secondary text-foreground hover:bg-fuchsia-500/20"}`}>
          {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold" data-testid={`track-name-${track.id}`}>{track.name}</div>
              <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                {fmtSize(track.size) && <span>{fmtSize(track.size)}</span>}
                {track.use_count > 0 && <span data-testid={`track-usecount-${track.id}`}>Used {track.use_count}×</span>}
                {track.last_used_at && (
                  <span className="flex items-center gap-1" data-testid={`track-lastused-${track.id}`}>
                    <CalendarClock className="h-3 w-3" /> Last used {fmtUsed(track.last_used_at)}
                  </span>
                )}
                {track.missing && <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400"><AlertTriangle className="h-3 w-3" /> Not in Dropbox</span>}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              {!track.missing && (
                <Button size="sm" variant="ghost" className="h-8 gap-1.5" title="Open in Dropbox"
                  data-testid={`track-dropbox-${track.id}`}
                  onClick={async () => {
                    try {
                      const { data } = await api.get(`/music/${track.id}/link`);
                      window.open(data.url, "_blank", "noopener");
                    } catch (err) { toast.error(apiError(err)); }
                  }}>
                  <ExternalLink className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Dropbox</span>
                </Button>
              )}
              <MarkUsedDialog track={track} onDone={() => onChanged(null)} />
              <Button size="sm" variant="ghost" className="h-8 gap-1.5" data-testid={`track-edit-${track.id}`}
                onClick={() => setEditing((e) => !e)}>
                <Pencil className="h-3.5 w-3.5" /> <span className="hidden sm:inline">{editing ? "Close" : "Edit"}</span>
              </Button>
            </div>
          </div>

          {!editing && (
            <>
              <StyleChips styles={track.styles} className="mt-2" />
              {(track.tags || []).length > 0 && (
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                  <Tag className="h-3 w-3" />
                  {track.tags.map((t) => <span key={t} className="rounded bg-secondary px-1.5 py-0.5">{t}</span>)}
                </div>
              )}
              {track.notes && <p className="mt-1.5 text-xs text-muted-foreground">{track.notes}</p>}
            </>
          )}

          {isPlaying && (
            <audio ref={audioRef} src={streamUrl} controls className="mt-3 w-full"
              data-testid={`track-audio-${track.id}`}
              onEnded={() => setPlayingId(null)}
              onError={() => toast.error("Couldn't play this track. Try syncing music again.")} />
          )}

          {(track.usages || []).length > 0 && (
            <div className="mt-2.5 space-y-1 border-t border-border pt-2">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Used on</div>
              {track.usages.map((u) => (
                <div key={u.id} className="flex items-center justify-between gap-2 text-xs" data-testid={`track-usage-${u.id}`}>
                  <button onClick={() => navigate(`/orders/${u.order_id}`)} className="flex min-w-0 items-center gap-1.5 hover:text-primary">
                    <span className="rounded bg-secondary px-1 py-0.5 text-[10px]">{u.deliverable_type}</span>
                    <span className="truncate">{u.address}</span>
                    <span className="font-mono text-muted-foreground">#{u.order_number}</span>
                  </button>
                  <button onClick={() => setPendingRemove(u)}
                    data-testid={`track-usage-remove-${u.id}`} className="text-muted-foreground hover:text-destructive">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <AlertDialog open={!!pendingRemove} onOpenChange={(o) => !o && setPendingRemove(null)}>
            <AlertDialogContent data-testid="usage-remove-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Remove this usage?</AlertDialogTitle>
                <AlertDialogDescription>
                  This removes “{track.name}” as used on {pendingRemove?.deliverable_type} · {pendingRemove?.address}. The track itself is not deleted.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="usage-remove-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="usage-remove-confirm"
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={async () => {
                    const u = pendingRemove;
                    setPendingRemove(null);
                    await api.delete(`/music/${track.id}/usage/${u.id}`);
                    onChanged(null);
                  }}>Remove</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {editing && (
            <div className="mt-3 space-y-3 rounded-lg border border-border bg-secondary/20 p-3">
              <div>
                <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Styles</div>
                <div className="flex flex-wrap gap-1.5">
                  {styleOptions.map((s) => (
                    <button key={s} onClick={() => toggleStyle(s)} data-testid={`style-toggle-${s}`}
                      className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${styles.includes(s) ? "border-fuchsia-500 bg-fuchsia-500/20 text-fuchsia-600 dark:text-fuchsia-300" : "border-border text-muted-foreground hover:bg-secondary"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Tags</div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {tags.map((t) => (
                    <span key={t} className="flex items-center gap-1 rounded bg-secondary px-1.5 py-0.5 text-[11px]">
                      {t}<button onClick={() => setTags(tags.filter((x) => x !== t))}><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                  <div className="flex items-center gap-1">
                    <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                      placeholder="add tag" className="h-7 w-28 text-xs" data-testid={`tag-input-${track.id}`} />
                    <Button size="sm" variant="outline" className="h-7 px-2" onClick={addTag}><Plus className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              </div>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)"
                className="min-h-[60px] text-sm" data-testid={`notes-input-${track.id}`} />
              <div className="flex justify-end">
                <Button size="sm" onClick={save} disabled={saving} data-testid={`track-save-${track.id}`} className="gap-1.5">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Music() {
  const navigate = useNavigate();
  const [tracks, setTracks] = useState([]);
  const [styleOptions, setStyleOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [q, setQ] = useState("");
  const [styleFilters, setStyleFilters] = useState([]);
  const [sort, setSort] = useState("recent_used");
  const [folderLink, setFolderLink] = useState(null);
  const [playingId, setPlayingId] = useState(null);

  const load = async () => {
    try {
      const params = [`sort=${sort}`];
      if (q.trim()) params.push(`q=${encodeURIComponent(q.trim())}`);
      if (styleFilters.length) params.push(`styles=${encodeURIComponent(styleFilters.join(","))}&match=all`);
      const { data } = await api.get(`/music?${params.join("&")}`);
      setTracks(data);
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/music/styles").then(({ data }) => setStyleOptions(data.styles)).catch(() => {});
    api.get("/dropbox/status").then(({ data }) => setFolderLink(data.music_folder_link)).catch(() => {});
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, styleFilters, sort]);

  const toggleStyleFilter = (s) => setStyleFilters((cur) => cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]);

  const syncMusic = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/music/sync");
      toast.success(`Synced — ${data.total} tracks${data.added ? ` (${data.added} new)` : ""}`);
      await load();
    } catch (err) { toast.error(apiError(err, "Sync failed")); }
    finally { setSyncing(false); }
  };

  const onChanged = (updated) => {
    if (updated) setTracks((cur) => cur.map((t) => (t.id === updated.id ? updated : t)));
    else load();
  };

  return (
    <div>
      <PageHeader title="Music" subtitle="Manage & preview the background tracks for your videos" testid="music-header">
        {folderLink && (
          <Button onClick={syncMusic} disabled={syncing} data-testid="music-sync-btn" className="gap-2">
            {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Sync music
          </Button>
        )}
      </PageHeader>
      <div className="p-6 md:p-8">
        {!folderLink ? (
          <div className="rounded-xl border border-dashed border-border py-16 text-center" data-testid="music-no-folder">
            <MusicIcon className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No music folder connected yet.</p>
            <Button className="mt-4 gap-2" onClick={() => navigate("/integrations")} data-testid="music-goto-integrations">
              <Link2 className="h-4 w-4" /> Add your Dropbox music folder
            </Button>
          </div>
        ) : (
          <>
            <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="music-search-input"
                  placeholder="Search by name, style or tag…" className="pl-9" />
              </div>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="sm:w-48" data-testid="music-sort-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value} data-testid={`music-sort-${o.value}`}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="mb-5 flex flex-wrap gap-1.5" data-testid="music-style-filters">
              <button onClick={() => setStyleFilters([])} data-testid="style-filter-all"
                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${styleFilters.length === 0 ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:bg-secondary"}`}>
                All
              </button>
              {styleOptions.map((s) => (
                <button key={s} onClick={() => toggleStyleFilter(s)} data-testid={`style-filter-${s}`}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${styleFilters.includes(s) ? "border-fuchsia-500 bg-fuchsia-500/20 text-fuchsia-600 dark:text-fuchsia-300" : "border-border text-muted-foreground hover:bg-secondary"}`}>
                  {s}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="flex h-48 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : tracks.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border py-16 text-center" data-testid="music-empty">
                <ListMusic className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {q || styleFilters.length ? "No tracks match your filters." : "No tracks yet — hit “Sync music” to pull them from Dropbox."}
                </p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="music-list">
                {tracks.map((t) => (
                  <TrackRow key={t.id} track={t} styleOptions={styleOptions}
                    playingId={playingId} setPlayingId={setPlayingId} onChanged={onChanged} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
